import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Webhook, Copy, RefreshCw, Check, ArrowUpRight, Send, AlertTriangle } from "lucide-react";

export default function WebhookSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const [testPayload, setTestPayload] = useState({
    contact_name: "Test Contact",
    business_name: "Test Business",
    email: `webhook-test-${Date.now()}@example.com`,
    phone: "+15550000001",
    source: "Manual test",
    status: "new",
    notes: "Test payload sent from the webhook settings tester",
  });
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/webhook/settings");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const copy = (label, value) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(label); toast.success(`${label} copied.`);
      setTimeout(() => setCopied(""), 1500);
    });
  };

  const regenerate = async () => {
    if (!window.confirm("Regenerate the API key? Existing Zapier / Make connections will need to be updated.")) return;
    setBusy(true);
    try {
      await api.post("/webhook/settings/regenerate");
      toast.success("API key regenerated.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const sendTestWebhook = async () => {
    if (!testPayload.email && !testPayload.phone) {
      return toast.error("Provide at least an email OR a phone (endpoint requires one).");
    }
    if (!window.confirm(`Send a real test POST to your live webhook? This will create/update a lead in the Pipeline (marked source="${testPayload.source}"). You can delete it after.`)) return;
    setTestSending(true);
    setTestResult(null);
    try {
      // Always hit the CURRENT origin's webhook path so preview tests preview + prod tests prod.
      // The display URL uses PUBLIC_SITE_URL (canonical for Zapier), but tests must match the DB
      // that issued this session's key.
      const testUrl = `${window.location.origin}/api/webhook/leads/${data.api_key}`;
      const r = await fetch(testUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(testPayload),
      });
      const bodyText = await r.text();
      let bodyJson = null;
      try { bodyJson = JSON.parse(bodyText); } catch { /* not json */ }
      setTestResult({
        status: r.status,
        ok: r.ok,
        matched: bodyJson?.matched,
        lead_id: bodyJson?.lead_id || bodyJson?.client_id,
        status_mapped: bodyJson?.status_mapped,
        source: bodyJson?.source,
        raw: bodyJson || bodyText.slice(0, 400),
        error: !r.ok ? (bodyJson?.detail || bodyText.slice(0, 200)) : null,
      });
      if (r.ok) {
        toast.success(`Webhook works · ${bodyJson?.matched || "ok"} · lead=${(bodyJson?.lead_id || bodyJson?.client_id || "").slice(0, 8)}…`);
      } else {
        toast.error(`HTTP ${r.status} — ${(bodyJson?.detail || bodyText).slice(0, 100)}`);
      }
    } catch (e) {
      setTestResult({ status: 0, ok: false, error: String(e).slice(0, 200) });
      toast.error("Network error — see console.");
    } finally {
      setTestSending(false);
    }
  };

  if (loading || !data) return <div className="p-10 text-neutral-500 text-sm">Loading webhook settings…</div>;

  return (
    <div className="p-10 max-w-4xl" data-testid="webhook-settings-page">
      <Toaster position="top-right" richColors />
      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><Webhook className="w-3.5 h-3.5" /> Integrations · Webhook Intake</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-2">Push leads in from anywhere</h1>
          <p className="text-neutral-500 max-w-2xl text-sm">
            Plug this URL into Zapier, Make, Apollo, or any tool that can POST JSON. New contacts land in the Pipeline tagged with the source. Existing contacts get updated — no duplicates.
          </p>
        </div>
        <button onClick={regenerate} disabled={busy} className="lf-btn-outline text-sm inline-flex items-center gap-2" data-testid="webhook-regenerate">
          <RefreshCw className="w-4 h-4" /> Regenerate key
        </button>
      </div>

      {/* Webhook URL */}
      <div className="bg-white border border-neutral-200 p-5 mb-4" data-testid="webhook-url-card">
        <div className="overline mb-2">Webhook URL</div>
        <div className="flex items-center gap-2 flex-wrap">
          <code className="flex-1 bg-neutral-50 border border-neutral-200 px-3 py-2 text-xs font-mono truncate min-w-0" data-testid="webhook-url">
            {data.webhook_url}
          </code>
          <button onClick={() => copy("URL", data.webhook_url)} className="lf-btn-outline text-xs px-3 py-2 flex items-center gap-1" data-testid="webhook-copy-url">
            {copied === "URL" ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
          </button>
        </div>
      </div>

      {/* API key */}
      <div className="bg-white border border-neutral-200 p-5 mb-4">
        <div className="overline mb-2">API key</div>
        <div className="flex items-center gap-2 flex-wrap">
          <code className="flex-1 bg-neutral-50 border border-neutral-200 px-3 py-2 text-xs font-mono truncate" data-testid="webhook-api-key">
            {data.api_key}
          </code>
          <button onClick={() => copy("Key", data.api_key)} className="lf-btn-outline text-xs px-3 py-2 flex items-center gap-1" data-testid="webhook-copy-key">
            {copied === "Key" ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
          </button>
        </div>
        <div className="text-xs text-neutral-500 mt-2">Keep secret. Anyone with this key can push into your Pipeline.</div>
      </div>

      {/* Payload docs */}
      <div className="bg-neutral-900 text-white p-5 mb-4">
        <div className="text-xs font-mono uppercase tracking-widest text-amber-300 mb-3">Request format</div>
        <div className="text-sm mb-3"><strong>{data.docs.method}</strong> · <code className="text-amber-200">{data.docs.content_type}</code></div>
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-1">Body fields</div>
        <div className="flex flex-wrap gap-2 mb-4">
          {data.docs.body_fields.map((f) => <span key={f} className="text-xs font-mono px-2 py-0.5 border border-neutral-700 bg-neutral-800">{f}</span>)}
        </div>
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-1">Accepted status values (auto-mapped)</div>
        <div className="flex flex-wrap gap-2 mb-4">
          {data.docs.status_values.map((s) => <span key={s} className="text-xs font-mono px-2 py-0.5 border border-neutral-700 bg-neutral-800">{s}</span>)}
        </div>
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-1">Example</div>
        <pre className="text-[11px] bg-black/50 border border-neutral-700 p-3 whitespace-pre-wrap break-all font-mono" data-testid="webhook-example-curl">{data.docs.example_curl}</pre>
      </div>

      {/* In-browser webhook tester */}
      <div className="bg-white border border-[#E24E1B] p-5 mb-4" data-testid="webhook-tester">
        <div className="flex items-center gap-2 mb-1">
          <Send className="w-4 h-4 text-[#E24E1B]" />
          <h2 className="font-display text-lg font-bold">Send a test webhook</h2>
        </div>
        <p className="text-xs text-neutral-500 mb-4">Fire a real POST at the URL above with a sample payload. Perfect for verifying Zapier/Symphony integrations without opening a terminal. A test lead will land in the Pipeline — delete after.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          {["contact_name", "business_name", "email", "phone", "source", "notes"].map((f) => (
            <label key={f} className="block">
              <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">{f}</div>
              <input
                type="text"
                value={testPayload[f] || ""}
                onChange={(e) => setTestPayload({ ...testPayload, [f]: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
                data-testid={`webhook-test-${f}`}
              />
            </label>
          ))}
          <label className="block">
            <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">status</div>
            <select
              value={testPayload.status}
              onChange={(e) => setTestPayload({ ...testPayload, status: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
              data-testid="webhook-test-status"
            >
              {(data.docs.status_values || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>

        <button
          onClick={sendTestWebhook}
          disabled={testSending}
          className="lf-btn-primary text-sm inline-flex items-center gap-2"
          data-testid="webhook-test-send-btn"
        >
          <Send className="w-4 h-4" /> {testSending ? "Sending…" : "Send test POST"}
        </button>

        {testResult && (
          <div className={`mt-4 p-3 border font-mono text-xs ${testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`} data-testid="webhook-test-result">
            <div className="font-semibold mb-1">
              HTTP {testResult.status} · {testResult.ok ? `success — ${testResult.matched || "ok"}` : "failed"}
            </div>
            {testResult.ok ? (
              <div className="space-y-0.5">
                <div>matched: <strong>{testResult.matched}</strong></div>
                {testResult.lead_id && <div>id: <strong>{testResult.lead_id}</strong></div>}
                <div>status_mapped: <strong>{testResult.status_mapped}</strong></div>
                <div>source recorded as: <strong>{testResult.source}</strong></div>
              </div>
            ) : (
              <div>{testResult.error}</div>
            )}
          </div>
        )}

        {testResult?.ok && (
          <div className="mt-3 flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 p-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>A test lead was created in your Pipeline. Head to <a href="/leads" className="underline">/leads</a> to review and delete it.</span>
          </div>
        )}
      </div>

      {/* Zapier + Make hint */}
      <div className="bg-white border border-neutral-200 p-5">
        <div className="overline mb-3">Zapier / Make</div>
        <p className="text-sm text-neutral-600">Use the built-in <strong>Webhooks by Zapier → POST</strong> action (or Make's HTTP module). Point it at the URL above, set body type to JSON, map your fields, done. Zero code.</p>
        <div className="mt-3 flex gap-3 text-xs">
          <a href="https://zapier.com/apps/webhook/integrations" target="_blank" rel="noopener noreferrer" className="text-coral hover:underline inline-flex items-center gap-1">Zapier docs <ArrowUpRight className="w-3 h-3" /></a>
          <a href="https://www.make.com/en/help/tools/http" target="_blank" rel="noopener noreferrer" className="text-coral hover:underline inline-flex items-center gap-1">Make docs <ArrowUpRight className="w-3 h-3" /></a>
        </div>
      </div>
    </div>
  );
}
